import { readonly, ref } from 'vue';
import InboxesAPI from 'dashboard/api/inboxes';

// Loaded lazily (like the ERP's own softphone hook does) so jssip only ships
// to agents who actually touch a Redlab Calls inbox.
let jsSipModulePromise = null;
const loadJsSIP = () => {
  if (!jsSipModulePromise) {
    jsSipModulePromise = import('jssip').then(mod => mod.default || mod);
  }
  return jsSipModulePromise;
};

// One entry per inboxId, each with its OWN JsSIP UA/registration -- an agent
// can be assigned more than one Redlab Calls inbox (a different extension
// per line) and needs to receive calls on all of them at once, so a single
// module-level UA (the original Fase 3 outbound-only design) isn't enough.
// Entry shape: { ua, currentSession, activeCallSid, awaitingOwnBridgeLeg,
// awaitingOwnBridgeLegTimer }
const sessionsByInbox = new Map();

let remoteAudioEl = null;
// Reactive per-inbox registration flags, Map<inboxId, boolean> wrapped in a
// ref so components can watch a specific inbox's state reactively.
const registeredInboxIds = ref(new Set());

// Mirrors TwilioVoiceClient's EventTarget-based pub/sub (see
// useCallSession.js's handleTwilioDisconnectedGlobal) so the calls store can
// be told to drop the card without this provider-agnostic-by-design
// composable importing Pinia directly.
export const asteriskCallEvents = new EventTarget();

const REGISTER_TIMEOUT_MS = 15000;
// True for a short window right after we originate our own outbound call on
// a given inbox, while we're expecting Asterisk's ring-back leg (see
// markOwnBridgeLegExpected). newRTCSession can't otherwise tell "Asterisk
// bridging my own outbound call" from "a genuine inbound call ringing me" --
// both arrive as an ordinary remote INVITE with no distinguishing header.
const OWN_BRIDGE_LEG_WINDOW_MS = 10000;

const ensureRemoteAudioElement = () => {
  if (remoteAudioEl) return remoteAudioEl;
  remoteAudioEl = document.createElement('audio');
  remoteAudioEl.id = 'redlab-calls-remote-audio';
  remoteAudioEl.autoplay = true;
  remoteAudioEl.playsInline = true;
  remoteAudioEl.style.display = 'none';
  document.body.appendChild(remoteAudioEl);
  return remoteAudioEl;
};

// Shared across every inbox's UA: only one call is ever actually "active"
// (answered/talking) at a time in this UI's model (calls store has a single
// isActive slot, no hold/multi-line UI exists yet) -- a single audio element
// is enough, whichever session confirms last simply owns it.
const attachRemoteAudio = session => {
  const pc = session.connection;
  if (!pc) return;
  pc.ontrack = event => {
    const stream = event.streams && event.streams[0];
    if (!stream) return;
    const el = ensureRemoteAudioElement();
    el.srcObject = stream;
    el.play().catch(() => {});
  };
};

const cleanupSession = entry => {
  entry.currentSession = null;
  if (remoteAudioEl) remoteAudioEl.srcObject = null;
  const callSid = entry.activeCallSid;
  entry.activeCallSid = null;
  if (callSid) {
    asteriskCallEvents.dispatchEvent(
      new CustomEvent('call:ended', { detail: { callSid } })
    );
  }
};

const bindSessionEvents = (session, entry) => {
  session.on('failed', () => cleanupSession(entry));
  session.on('ended', () => cleanupSession(entry));
  session.on('confirmed', () => attachRemoteAudio(session));
};

// The ring-back leg Asterisk generates when it bridges a call WE originated
// (Adapter#initiate_call -> POST /calls -> originateCall). Auto-answer
// without agent interaction -- from their point of view they already "made
// the call" by clicking, they are not picking up their own phone.
const handleInboundBridgeLeg = (session, entry) => {
  entry.currentSession = session;
  bindSessionEvents(session, entry);
  session.answer({ mediaConstraints: { audio: true, video: false } });
};

// A genuine inbound call (someone dialed this extension's DID/number, not
// us bridging our own outbound call). Do NOT auto-answer -- store the
// pending session so answer()/hangup() can act on it once the agent
// responds to the ring UI (driven by the voice_call.incoming cable
// broadcast, see RedlabCalls::IncomingCallService on the backend).
const handleGenuineIncomingCall = (session, entry) => {
  entry.currentSession = session;
  bindSessionEvents(session, entry);
};

const registerUA = (JsSIP, sipConfig, entry) =>
  new Promise((resolve, reject) => {
    const socket = new JsSIP.WebSocketInterface(sipConfig.wsServer);
    const newUa = new JsSIP.UA({
      sockets: [socket],
      uri: sipConfig.sipUri,
      password: sipConfig.sipPassword,
      register: true,
      register_expires: 120,
      connection_recovery_min_interval: 2,
      connection_recovery_max_interval: 30,
      user_agent: 'Redchat/1.0',
    });

    const timeout = setTimeout(() => {
      newUa.stop();
      reject(new Error('Tiempo de espera agotado registrando el softphone'));
    }, REGISTER_TIMEOUT_MS);

    newUa.on('registered', () => {
      clearTimeout(timeout);
      entry.isRegistered.value = true;
      resolve(newUa);
    });
    newUa.on('registrationFailed', e => {
      clearTimeout(timeout);
      reject(new Error(e?.cause || 'Registro SIP fallido'));
    });
    newUa.on('unregistered', () => {
      entry.isRegistered.value = false;
    });
    newUa.on('disconnected', () => {
      entry.isRegistered.value = false;
    });
    newUa.on('newRTCSession', data => {
      if (data.originator !== 'remote') return;
      if (entry.awaitingOwnBridgeLeg) {
        entry.awaitingOwnBridgeLeg = false;
        clearTimeout(entry.awaitingOwnBridgeLegTimer);
        handleInboundBridgeLeg(data.session, entry);
        return;
      }
      handleGenuineIncomingCall(data.session, entry);
    });

    newUa.start();
  });

const getOrCreateEntry = inboxId => {
  let entry = sessionsByInbox.get(inboxId);
  if (!entry) {
    entry = {
      ua: null,
      currentSession: null,
      activeCallSid: null,
      awaitingOwnBridgeLeg: false,
      awaitingOwnBridgeLegTimer: null,
      isRegistered: ref(false),
    };
    sessionsByInbox.set(inboxId, entry);
  }
  return entry;
};

export function useAsteriskCallSession() {
  // Idempotent: a second call for the same inbox while already registered is
  // a no-op, so ConversationCallButton (and the proactive registration on
  // dashboard mount, see useCallSession.js) can call this freely without
  // re-registering mid-session. Registering inbox A never tears down inbox
  // B's UA -- each inbox keeps its own independent registration.
  const registerForInbox = async inboxId => {
    if (!inboxId) return;
    const entry = getOrCreateEntry(inboxId);
    if (entry.ua && entry.isRegistered.value) return;

    const JsSIP = await loadJsSIP();
    const { data: sipConfig } =
      await InboxesAPI.getRedlabCallsSipConfig(inboxId);
    entry.ua = await registerUA(JsSIP, sipConfig, entry);
    registeredInboxIds.value = new Set(registeredInboxIds.value).add(inboxId);
  };

  const isRegisteredForInbox = inboxId =>
    !!sessionsByInbox.get(inboxId)?.isRegistered?.value;

  // Called right after callsStore.addCall for the call this UA is about to
  // bridge, so cleanupSession knows which card to ask the store to drop
  // regardless of how quickly the inbound leg answers and ends.
  const setActiveCallSid = (inboxId, callSid) => {
    getOrCreateEntry(inboxId).activeCallSid = callSid;
  };

  // Called right before originating an outbound call (ConversationCallButton),
  // so the next remote INVITE this inbox's UA sees is treated as our own
  // ring-back leg (auto-answered) instead of a genuine inbound call needing
  // the agent's accept/reject. Self-clears after OWN_BRIDGE_LEG_WINDOW_MS in
  // case the origination fails at Asterisk and no bridge leg ever arrives --
  // otherwise a real inbound call ringing shortly after would be silently
  // auto-answered.
  const markOwnBridgeLegExpected = inboxId => {
    const entry = getOrCreateEntry(inboxId);
    entry.awaitingOwnBridgeLeg = true;
    clearTimeout(entry.awaitingOwnBridgeLegTimer);
    entry.awaitingOwnBridgeLegTimer = setTimeout(() => {
      entry.awaitingOwnBridgeLeg = false;
    }, OWN_BRIDGE_LEG_WINDOW_MS);
  };

  // Agent accepted a genuine inbound call from the ring UI (see
  // handleGenuineIncomingCall -- currentSession is already set, just not
  // answered yet).
  const answer = inboxId => {
    const entry = sessionsByInbox.get(inboxId);
    if (!entry?.currentSession) return;
    // Events were already bound in handleGenuineIncomingCall when the
    // session first arrived -- don't re-bind here, that would double the
    // failed/ended/confirmed listeners.
    entry.currentSession.answer({
      mediaConstraints: { audio: true, video: false },
    });
  };

  const hangup = inboxId => {
    const entry = sessionsByInbox.get(inboxId);
    if (!entry?.currentSession) return;
    if (entry.currentSession.isEstablished()) {
      entry.currentSession.terminate();
    } else {
      entry.currentSession.terminate({
        status_code: 486,
        reason_phrase: 'Busy Here',
      });
    }
    cleanupSession(entry);
  };

  return {
    registeredInboxIds: readonly(registeredInboxIds),
    isRegisteredForInbox,
    registerForInbox,
    setActiveCallSid,
    markOwnBridgeLegExpected,
    answer,
    hangup,
  };
}

// Exposed as a plain function (like setWhatsappCallMuted) so FloatingCallWidget
// can mute the active leg without knowing which provider is live. Needs
// inboxId now that sessions are per-inbox -- callers already have the active
// call's inboxId at hand (calls store entries carry it).
export const setAsteriskCallMuted = (inboxId, muted) => {
  const entry = sessionsByInbox.get(inboxId);
  if (!entry?.currentSession) return false;
  if (muted) {
    entry.currentSession.mute({ audio: true });
  } else {
    entry.currentSession.unmute({ audio: true });
  }
  return muted;
};

// Exposed as a plain function (like cleanupWhatsappSession) so calls store's
// teardownByProvider can end the SIP leg without mounting a composable.
export const hangupAsteriskCall = inboxId => {
  const entry = sessionsByInbox.get(inboxId);
  if (!entry?.currentSession) return;
  try {
    if (entry.currentSession.isEstablished()) {
      entry.currentSession.terminate();
    } else {
      entry.currentSession.terminate({
        status_code: 486,
        reason_phrase: 'Busy Here',
      });
    }
  } finally {
    cleanupSession(entry);
  }
};
